import React, { useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { authAPI } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/roles';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { User, Shield, MapPin, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

export default function AccountTab() {
  const { user, fetchMe } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }
    
    // Validate password policy
    if (newPassword.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten met minstens 1 hoofdletter, 1 kleine letter en 1 cijfer.');
      return;
    }
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasDigit = /[0-9]/.test(newPassword);
    if (!hasUpper || !hasLower || !hasDigit) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten met minstens 1 hoofdletter, 1 kleine letter en 1 cijfer.');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.changePassword(currentPassword, newPassword);
      if (res.ok) {
        toast.success('Wachtwoord bijgewerkt');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const d = await res.clone().json();
        setError(d.detail || 'Fout bij wachtwoord wijzigen');
      }
    } catch {
      setError('Fout bij wachtwoord wijzigen');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Profile card */}
      <Card className="border border-slate-200" data-testid="account-profile-card">
        <CardHeader>
          <CardTitle className="text-lg">Mijn profiel</CardTitle>
          <CardDescription>Je accountgegevens op het TW1 Identity Platform</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white flex-shrink-0"
              style={{ backgroundColor: user?.avatar_kleur || '#1a2332' }}
            >
              {user?.naam?.charAt(0)?.toUpperCase()}
            </div>
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium">{user?.naam}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{user?.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-400" />
                <Badge variant="outline">{ROLE_LABELS[user?.globale_rol] || user?.globale_rol}</Badge>
              </div>
              {user?.locations?.length > 0 && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <div className="flex flex-wrap gap-1">
                    {user.locations.map(l => (
                      <Badge key={l} variant="outline" className="text-xs">{l}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change password */}
      <Card className="border border-slate-200" data-testid="account-change-password-form">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="w-5 h-5" /> Wachtwoord wijzigen
          </CardTitle>
          <CardDescription>Wijzig je wachtwoord voor het TW1 Identity Platform</CardDescription>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded mb-4">{error}</p>}
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-pw">Huidig wachtwoord</Label>
              <div className="relative">
                <Input
                  id="current-pw"
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pw">Nieuw wachtwoord</Label>
              <Input
                id="new-pw"
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pw">Bevestig nieuw wachtwoord</Label>
              <Input
                id="confirm-pw"
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press transition-colors duration-150"
              data-testid="account-change-password-submit"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig...</> : 'Wachtwoord wijzigen'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
